import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SiteShell from "../components/site/SiteShell";
import { Container } from "../components/site/ui";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function ClientLogin() {
  const { authenticate, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(location.hash?.includes("session_id="));
  const processed = useRef(false);

  // Handle Emergent Google callback (#session_id=...)
  useEffect(() => {
    if (!location.hash?.includes("session_id=")) return;
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        authenticate(data.access_token, data.user);
        window.history.replaceState(null, "", "/client-login");
        navigate("/client", { replace: true });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed.");
        setProcessing(false);
      }
    })();
  }, [location.hash, authenticate, navigate]);

  useEffect(() => { if (user && user.role) navigate(user.role === "admin" ? "/admin" : "/client", { replace: true }); }, [user, navigate]);

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/client-login"; // REMINDER: DO NOT HARDCODE THE URL OR ADD FALLBACKS
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", form);
        authenticate(data.access_token, data.user);
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        authenticate(data.access_token, data.user);
      }
      navigate("/client", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-xl border border-white/10 bg-white/[0.03] pl-11 pr-4 py-3 text-white placeholder:text-zinc-500 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition";

  if (processing) {
    return (
      <SiteShell title="Signing in — GoBuildWeb">
        <Container className="grid min-h-[60vh] place-items-center py-40"><Loader2 className="animate-spin text-gold-500" size={32} /></Container>
      </SiteShell>
    );
  }

  return (
    <SiteShell title="Client Login — GoBuildWeb" description="Sign in to your GoBuildWeb client area to manage your website, services, billing and support." testId="client-login-page">
      <section className="pt-[72px]">
        <Container className="grid min-h-[70vh] place-items-center py-16">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-8">
            <h1 className="font-display text-3xl font-bold text-white">{mode === "login" ? "Client Login" : "Create your account"}</h1>
            <p className="mt-2 text-sm text-zinc-400">{mode === "login" ? "Welcome back. Access your website, billing and support." : "Set up your client area in seconds."}</p>

            <button onClick={googleLogin} className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white px-4 py-3 font-semibold text-black transition hover:bg-zinc-100" data-testid="google-login">
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" /> Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3 text-xs text-zinc-600"><div className="h-px flex-1 bg-white/10" /> OR <div className="h-px flex-1 bg-white/10" /></div>

            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="client-auth-form">
              {mode === "register" && (
                <div className="relative"><User size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" /><input className={input} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="auth-name" /></div>
              )}
              <div className="relative"><Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" /><input type="email" className={input} placeholder="Email" value={form.email} onChange={set("email")} data-testid="auth-email" /></div>
              <div className="relative"><Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" /><input type="password" className={input} placeholder="Password" value={form.password} onChange={set("password")} data-testid="auth-password" /></div>
              <button type="submit" disabled={loading} className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 px-6 py-3.5 font-semibold text-black transition hover:bg-gold-400 disabled:opacity-60" data-testid="auth-submit">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-zinc-400">
              {mode === "login" ? "New to GoBuildWeb? " : "Already have an account? "}
              <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-gold-400 hover:text-gold-300" data-testid="auth-toggle">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>
            <p className="mt-2 text-center text-xs text-zinc-600">Don't have a project yet? <Link to="/get-started" className="text-gold-500 hover:underline">Get My Website</Link></p>
          </div>
        </Container>
      </section>
    </SiteShell>
  );
}
